import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { LikesProvider } from "@/context/LikesContext";
import { Navbar } from "@/components/Navbar";
import { TrialBanner } from "@/components/TrialBanner";
import Home from "@/pages/Home";
import Explore from "@/pages/Explore";
import AuthPage from "@/pages/AuthPage";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import ShowRecap from "@/pages/ShowRecap";
import Pricing from "@/pages/Pricing";
import VendorDashboard from "@/pages/VendorDashboard";
import Account from "@/pages/Account";
import PaymentResult from "@/pages/PaymentResult";
import Trip from "@/pages/Trip";
import SharedTrip from "@/pages/SharedTrip";
import TravelerProfile from "@/pages/TravelerProfile";
import Following from "@/pages/Following";
import OwnerDashboard from "@/pages/OwnerDashboard";
import FamilyProfiles from "@/pages/FamilyProfiles";
import Notifications from "@/pages/Notifications";
import VendorReviewsPage from "@/pages/VendorReviewsPage";
import AppReviews from "@/pages/AppReviews";
import { RatingPrompt } from "@/components/RatingPrompt";
import { ReminderWatcher } from "@/components/ReminderWatcher";
import { InstallBanner } from "@/components/InstallApp";
import { OfflineBanner } from "@/components/OfflineBanner";

function Protected({ children, role }) {
  const { user } = useAuth();
  const { pathname } = useLocation();
  if (user === null) return <div className="min-h-[60vh]" />;
  if (!user) return <Navigate to="/login" replace />;
  if (role && user.role !== role && user.role !== "admin") return <Navigate to="/" replace />;
  if (user.role === "user" && user.family_plan && !user.active_profile && !["/profiles", "/account"].includes(pathname)) return <Navigate to="/profiles" replace />;
  return children;
}

function App() {
  return (
    <AuthProvider>
      <LikesProvider>
      <BrowserRouter>
        <div className="min-h-screen bg-[#0B0F19] text-white">
          <TrialBanner />
          <ReminderWatcher />
          <RatingPrompt />
          <OfflineBanner />
          <Navbar />
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/explore" element={<Protected><Explore /></Protected>} />
            <Route path="/login" element={<AuthPage mode="login" />} />
            <Route path="/register" element={<AuthPage mode="register" />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/trip" element={<Protected><Trip /></Protected>} />
            <Route path="/recap/:rid" element={<Protected><ShowRecap /></Protected>} />
            <Route path="/share/:token" element={<SharedTrip />} />
            <Route path="/admin" element={<Protected role="admin"><OwnerDashboard /></Protected>} />
            <Route path="/following" element={<Protected><Following /></Protected>} />
            <Route path="/traveler/:id" element={<Protected><TravelerProfile /></Protected>} />
            <Route path="/pricing" element={<Pricing />} />
            <Route path="/app-reviews" element={<AppReviews />} />
            <Route path="/vendor" element={<Protected role="vendor"><VendorDashboard /></Protected>} />
            <Route path="/vendor/reviews" element={<Protected role="vendor"><VendorReviewsPage /></Protected>} />
            <Route path="/notifications" element={<Protected><Notifications /></Protected>} />
            <Route path="/account" element={<Protected><Account /></Protected>} />
            <Route path="/profiles" element={<Protected><FamilyProfiles /></Protected>} />
            <Route path="/payment/success" element={<PaymentResult success />} />
            <Route path="/payment/cancel" element={<PaymentResult />} />
          </Routes>
          <footer className="border-t border-[#26334D] mt-24 py-10 px-6 text-sm text-slate-500">
            <div className="max-w-7xl mx-auto flex flex-col sm:flex-row justify-between gap-3">
              <span>VacaVibe — your local insider, anywhere in the world.</span>
              <span>Listings refresh daily. Always confirm times with the venue before you go.</span>
            </div>
          </footer>
        </div>
        <InstallBanner />
        <Toaster theme="dark" position="top-center" richColors />
      </BrowserRouter>
      </LikesProvider>
    </AuthProvider>
  );
}

export default App;
